import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Typography,
} from "@mui/material";
import FlashOnRoundedIcon from "@mui/icons-material/FlashOnRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { trustBudTokens } from "../../theme/tokens";
import LoadingState from "../common/LoadingState";
import { getSprintWindow } from "../../features/shared/selectors/behaviorSummary";
import {
  daysBetweenLocalDates,
  formatTrustBudDateLabel,
} from "../../features/shared/utils/trustbudDates";

function sectionCardSx(extra = {}) {
  return {
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: "1px solid rgba(215, 226, 238, 0.94)",
    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
    ...extra,
  };
}

function iconBadgeSx(backgroundColor, color) {
  return {
    width: 40,
    height: 40,
    borderRadius: "8px",
    display: "grid",
    placeItems: "center",
    backgroundColor,
    color,
    flexShrink: 0,
  };
}

function buildSprintGroups(connections) {
  const needsDecision = [];
  const active = [];
  const recent = [];
  const startable = [];

  (connections || []).forEach((connection) => {
    const hasActiveSprint = Boolean(connection?.activeSprint?.id);
    const latestSprint = connection?.latestSprint || null;

    if (connection?.needsRenewal && !connection?.viewerReviewed) {
      needsDecision.push(connection);
      return;
    }

    if (hasActiveSprint) {
      active.push(connection);
      return;
    }

    if (latestSprint?.id) {
      recent.push(connection);
      return;
    }

    if (!hasActiveSprint) {
      startable.push(connection);
    }
  });

  recent.sort((left, right) => {
    const leftValue = left?.latestSprint?.end_date || left?.latestSprint?.start_date || "";
    const rightValue = right?.latestSprint?.end_date || right?.latestSprint?.start_date || "";
    return String(rightValue).localeCompare(String(leftValue));
  });

  return { needsDecision, active, recent, startable };
}

function getPartnerActivityLabel(item) {
  if (item?.sprintBehavior?.shared_activity_today) {
    return "Both showed up today";
  }
  if (item?.sprintBehavior?.partner_acted_today) {
    return "They showed up today";
  }

  const localDate = item?.sprintBehavior?.local_date || null;
  const lastActivity = item?.sprintBehavior?.partner_last_activity_at || null;
  const dayDelta = lastActivity && localDate ? daysBetweenLocalDates(lastActivity, localDate) : null;
  if (dayDelta === 0) return "Active today";
  if (dayDelta === 1) return "Last activity yesterday";
  if (typeof dayDelta === "number" && dayDelta > 1) {
    return `Last activity ${formatTrustBudDateLabel(lastActivity)}`;
  }

  return item?.statusDetail || "Quiet today";
}

function getSprintContext(item) {
  const sprint = item?.activeSprint || item?.latestSprint || null;
  const window = getSprintWindow({
    behaviorSummary: item?.behaviorSummary,
    sprint,
    sourceLabel: `connections-sprints-${item?.connectionId || item?.id || "unknown"}`,
  });

  const startDate = window.startDate;
  const localDate = item?.sprintBehavior?.local_date || null;
  const durationDays = Number(sprint?.duration_days || 0);
  const elapsed = startDate && localDate ? daysBetweenLocalDates(startDate, localDate) : null;
  const dayNumber =
    typeof elapsed === "number" && elapsed >= 0
      ? Math.min(Math.max(elapsed + 1, 1), durationDays || elapsed + 1)
      : null;

  let progressLabel = "";
  if (window.endsToday) {
    progressLabel = "Ends today";
  } else if (window.hasEnded) {
    progressLabel = window.endLabel ? `Ended ${window.endLabel}` : "Ended";
  } else if (dayNumber && durationDays) {
    progressLabel = `Day ${dayNumber} of ${durationDays}`;
  } else if (window.endChipLabel) {
    progressLabel = window.endChipLabel;
  }

  return {
    sprint,
    window,
    progressLabel,
  };
}

function getRecentSignal(item) {
  const sharedDays = Number(item?.sprintBehavior?.shared_days_last_7 || 0);
  if (sharedDays >= 3) return "Good stretch";
  if (sharedDays >= 1) return "Worth restarting";
  return "Short run";
}

function getRecentEndedLabel(item) {
  const localDate = item?.sprintBehavior?.local_date || null;
  const endDate = item?.latestSprint?.end_date || null;
  const prefix = Number(item?.sprintCount || 0) > 1 ? "Latest Accountability ended" : "Ended";
  const elapsed = endDate && localDate ? daysBetweenLocalDates(endDate, localDate) : null;
  if (elapsed === 0) return `${prefix} today`;
  if (elapsed === 1) return `${prefix} yesterday`;
  if (typeof elapsed === "number" && elapsed < 14) {
    return `${prefix} ${elapsed} days ago`;
  }
  if (typeof elapsed === "number" && elapsed < 28) {
    return `${prefix} ${Math.round(elapsed / 7)} week${Math.round(elapsed / 7) === 1 ? "" : "s"} ago`;
  }
  return endDate ? `${prefix} ${formatTrustBudDateLabel(endDate)}` : `${prefix} recently`;
}

function ProfileLink({ item, onOpenProfile, children, sx = {} }) {
  const openProfile = () => {
    if (!item?.connectionId) return;
    onOpenProfile?.(item);
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={openProfile}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openProfile();
        }
      }}
      sx={{
        cursor: "pointer",
        borderRadius: "8px",
        transition: "background-color 140ms ease, box-shadow 140ms ease",
        "&:hover": {
          backgroundColor: "rgba(23, 52, 93, 0.045)",
          boxShadow: "inset 0 0 0 1px rgba(23, 52, 93, 0.04)",
        },
        "&:focus-visible": {
          outline: "2px solid rgba(23, 52, 93, 0.18)",
          outlineOffset: 4,
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function SprintsHeader({ onChooseTrustBud }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2.4 }}>
      <Box>
        <Typography sx={{ fontSize: "2.15rem", lineHeight: 1.1, fontWeight: 850, letterSpacing: 0, color: "#102033" }}>
          Accountability
        </Typography>
        <Typography sx={{ mt: 0.55, fontSize: "1rem", color: "#5a6f87", lineHeight: 1.45 }}>
          Short accountability windows you start with one TrustBud.
        </Typography>
      </Box>

      {onChooseTrustBud ? (
        <Button
          variant="contained"
          onClick={onChooseTrustBud}
          startIcon={<AddRoundedIcon />}
          sx={{
            minHeight: 46,
            px: 2.15,
            borderRadius: "8px",
            backgroundColor: trustBudTokens.colors.brand.navy700,
            boxShadow: "0 16px 30px rgba(23, 52, 93, 0.22)",
            fontSize: "0.98rem",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": {
              backgroundColor: trustBudTokens.colors.brand.navy800,
            },
          }}
        >
          Start Accountability
        </Button>
      ) : null}
    </Box>
  );
}

function SprintDecisionCard({ item, onReview, onOpenProfile }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "52px minmax(0, 1fr)", sm: "56px minmax(0, 1fr) auto" },
        gap: 1.5,
        alignItems: "center",
        py: 1.6,
        borderTop: "1px solid rgba(232, 238, 246, 0.92)",
        "&:first-of-type": { borderTop: "none", pt: 0.2 },
      }}
    >
      <ProfileLink item={item} onOpenProfile={onOpenProfile} sx={{ width: 52, height: 52, borderRadius: "50%" }}>
        <Avatar src={item.avatar || ""} sx={{ width: 52, height: 52, bgcolor: "#d7d0c8", fontWeight: 700 }}>
          {item.name?.slice(0, 1)?.toUpperCase()}
        </Avatar>
      </ProfileLink>

      <Box sx={{ minWidth: 0 }}>
        <ProfileLink item={item} onOpenProfile={onOpenProfile} sx={{ width: "fit-content", maxWidth: "100%" }}>
          <Typography sx={{ fontSize: "1.02rem", fontWeight: 700, color: "#14243d", lineHeight: 1.2, "&:hover": { color: trustBudTokens.colors.brand.navy700, textDecoration: "underline" } }}>
            {item.name}
          </Typography>
        </ProfileLink>
        <Typography sx={{ mt: 0.5, fontSize: "0.95rem", fontWeight: 700, color: "#cf5a5f" }}>
          Accountability ended
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: "0.95rem", color: "#5e728a", lineHeight: 1.35 }}>
          Decide whether to restart or close it.
        </Typography>
      </Box>

      <Button
        variant="outlined"
        onClick={() => onReview(item)}
        sx={{
          gridColumn: { xs: "1 / -1", sm: "auto" },
          minWidth: 132,
          width: { xs: "100%", sm: "auto" },
          minHeight: 42,
          borderRadius: "16px",
          borderColor: "rgba(23, 52, 93, 0.16)",
          color: trustBudTokens.colors.brand.navy700,
          fontWeight: 700,
          textTransform: "none",
        }}
      >
        Review
      </Button>
    </Box>
  );
}

function ActiveSprintRow({ item, onOpenSprint, onMessage, onOpenProfile }) {
  const { progressLabel } = getSprintContext(item);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "54px minmax(0, 1fr)", md: "60px minmax(0, 1fr) auto" },
        gap: 1.6,
        alignItems: "center",
        py: 1.55,
        borderTop: "1px solid rgba(232, 238, 246, 0.92)",
        "&:first-of-type": { borderTop: "none", pt: 0.2 },
      }}
    >
      <ProfileLink item={item} onOpenProfile={onOpenProfile} sx={{ position: "relative", width: 54, height: 54, borderRadius: "50%" }}>
        <Avatar src={item.avatar || ""} sx={{ width: 54, height: 54, bgcolor: "#d7d0c8", fontWeight: 700 }}>
          {item.name?.slice(0, 1)?.toUpperCase()}
        </Avatar>
        <Box
          sx={{
            position: "absolute",
            right: -1,
            bottom: -1,
            width: 15,
            height: 15,
            borderRadius: "50%",
            backgroundColor: item.dotColor,
            border: "2px solid #fff",
          }}
        />
      </ProfileLink>

      <Box sx={{ minWidth: 0 }}>
        <ProfileLink item={item} onOpenProfile={onOpenProfile} sx={{ width: "fit-content", maxWidth: "100%" }}>
          <Typography sx={{ fontSize: "1.02rem", fontWeight: 700, color: "#13233d", lineHeight: 1.2, "&:hover": { color: trustBudTokens.colors.brand.navy700, textDecoration: "underline" } }}>
            {item.name}
          </Typography>
        </ProfileLink>
        <Typography sx={{ mt: 0.45, fontSize: "0.93rem", color: "#44607c", lineHeight: 1.3 }}>
          {getPartnerActivityLabel(item)}
        </Typography>
        <Typography sx={{ mt: 0.65, fontSize: "0.88rem", color: trustBudTokens.colors.brand.navy700, fontWeight: 700 }}>
          {progressLabel || "Accountability live"}
        </Typography>
        <Typography sx={{ mt: 0.45, fontSize: "1rem", color: "#14243d", fontWeight: 700, lineHeight: 1.2 }}>
          {item.goalTitle || "Shared focus"}
        </Typography>
        {item.goalSubtitle ? (
          <Typography
            sx={{
              mt: 0.32,
              fontSize: "0.92rem",
              color: "#5e728a",
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.goalSubtitle}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, gridColumn: { xs: "1 / -1", md: "auto" }, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          onClick={() => onMessage(item)}
          startIcon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 40,
            flex: { xs: "1 1 132px", md: "0 0 auto" },
            px: 1.4,
            borderRadius: "15px",
            borderColor: "rgba(23, 52, 93, 0.14)",
            color: "#50647e",
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          Message
        </Button>
        <Button
          variant="contained"
          onClick={() => onOpenSprint(item)}
          sx={{
            minHeight: 40,
            flex: { xs: "1 1 132px", md: "0 0 auto" },
            px: 1.6,
            borderRadius: "15px",
            backgroundColor: trustBudTokens.colors.brand.navy700,
            fontWeight: 700,
            textTransform: "none",
            "&:hover": {
              backgroundColor: trustBudTokens.colors.brand.navy800,
            },
          }}
        >
          Open Accountability
        </Button>
      </Box>
    </Box>
  );
}

function RecentSprintRow({ item, onStartAgain, onReview, onOpenProfile }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "52px minmax(0, 1fr)", md: "56px minmax(0, 1fr) auto" },
        gap: 1.4,
        alignItems: "center",
        py: 1.5,
        borderTop: "1px solid rgba(232, 238, 246, 0.92)",
        "&:first-of-type": { borderTop: "none", pt: 0.2 },
      }}
    >
      <ProfileLink item={item} onOpenProfile={onOpenProfile} sx={{ width: 50, height: 50, borderRadius: "50%" }}>
        <Avatar src={item.avatar || ""} sx={{ width: 50, height: 50, bgcolor: "#d7d0c8", fontWeight: 700 }}>
          {item.name?.slice(0, 1)?.toUpperCase()}
        </Avatar>
      </ProfileLink>

      <Box sx={{ minWidth: 0 }}>
        <ProfileLink item={item} onOpenProfile={onOpenProfile} sx={{ width: "fit-content", maxWidth: "100%" }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#13233d", lineHeight: 1.2, "&:hover": { color: trustBudTokens.colors.brand.navy700, textDecoration: "underline" } }}>
            {item.name}
          </Typography>
        </ProfileLink>
        <Typography sx={{ mt: 0.45, fontSize: "0.92rem", color: "#5e728a", lineHeight: 1.3 }}>
          {getRecentEndedLabel(item)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.7, flexWrap: "wrap" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              minHeight: 28,
              px: 1.1,
              borderRadius: "999px",
              backgroundColor: "#eef5ff",
              color: trustBudTokens.colors.brand.navy700,
              fontSize: "0.82rem",
              fontWeight: 700,
            }}
          >
            {getRecentSignal(item)}
          </Box>
          {item.goalTitle ? (
            <Typography
              sx={{
                fontSize: "0.9rem",
                color: "#5e728a",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.goalTitle}
            </Typography>
          ) : null}
          {Number(item?.sprintCount || 0) > 1 ? (
            <Typography sx={{ fontSize: "0.86rem", color: "#7a8da4", fontWeight: 700 }}>
              {item.sprintCount} Accountability agreements together
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, gridColumn: { xs: "1 / -1", md: "auto" }, flexWrap: "wrap" }}>
        <Button
          variant="text"
          onClick={() => onReview(item)}
          sx={{ minWidth: 64, color: "#50647e", fontWeight: 700, textTransform: "none", flex: { xs: "1 1 104px", md: "0 0 auto" } }}
        >
          Review
        </Button>
        <Button
          variant="outlined"
          onClick={() => onStartAgain(item)}
          startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 18 }} />}
          sx={{
            minWidth: 120,
            minHeight: 40,
            flex: { xs: "1 1 142px", md: "0 0 auto" },
            borderRadius: "15px",
            borderColor: "rgba(23, 52, 93, 0.16)",
            color: trustBudTokens.colors.brand.navy700,
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          Start again
        </Button>
      </Box>
    </Box>
  );
}

function SectionCard({ icon, title, children, actionLabel, onActionPress }) {
  return (
    <Box sx={sectionCardSx({ p: 2.25 })}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.4, mb: 1.45 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
          <Box sx={iconBadgeSx("#eef5ff", trustBudTokens.colors.brand.navy700)}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, letterSpacing: -0.45, color: "#13233d" }}>
            {title}
          </Typography>
        </Box>
        {actionLabel && onActionPress ? (
          <ButtonBase onClick={onActionPress} sx={{ color: trustBudTokens.colors.brand.navy700, fontSize: "0.95rem", fontWeight: 700, gap: 0.35 }}>
            {actionLabel}
            <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
          </ButtonBase>
        ) : null}
      </Box>
      {children}
    </Box>
  );
}

function SprintsEmptyState({ onOpenCircle, onFindPeople }) {
  return (
    <Box sx={sectionCardSx({ p: 3.1, textAlign: "center" })}>
      <Typography sx={{ fontSize: "1.4rem", fontWeight: 850, letterSpacing: 0, color: "#102033" }}>
        No Accountability yet
      </Typography>
      <Typography sx={{ mt: 0.85, fontSize: "1rem", lineHeight: 1.55, color: "#5e728a" }}>
        Start one with someone in your circle when you&apos;re ready.
      </Typography>
      <Box sx={{ mt: 2.1, display: "flex", justifyContent: "center", gap: 1.1, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          onClick={onOpenCircle}
          sx={{
            minHeight: 44,
            px: 2,
            borderRadius: "8px",
            backgroundColor: trustBudTokens.colors.brand.navy700,
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          Open Connections
        </Button>
        <Button
          variant="outlined"
          onClick={onFindPeople}
          sx={{
            minHeight: 44,
            px: 2,
            borderRadius: "8px",
            borderColor: "rgba(23, 52, 93, 0.16)",
            color: trustBudTokens.colors.brand.navy700,
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          Find people
        </Button>
      </Box>
    </Box>
  );
}

export default function SprintsTab({
  connections,
  loading = false,
  onStartSprint,
  onOpenSprint,
  onMessage,
  onOpenProfile,
  onReview,
  onOpenCircle,
  onStartChallengeFlow,
  onFindPeople,
}) {
  const [showAllRecent, setShowAllRecent] = useState(false);
  const { needsDecision, active, recent } = useMemo(
    () => buildSprintGroups(connections),
    [connections]
  );

  const visibleRecent = showAllRecent ? recent : recent.slice(0, 4);
  const hasAnyContent = needsDecision.length > 0 || active.length > 0 || recent.length > 0;

  return (
    <Box sx={{ display: "grid", gap: 2.2 }}>
      <SprintsHeader
        onChooseTrustBud={onStartChallengeFlow || onOpenCircle}
      />

      {loading && !hasAnyContent ? (
        <LoadingState
          title="Preparing Accountability"
          body="Checking active, recent, and ready-to-review agreements."
          rows={3}
          compact
        />
      ) : !hasAnyContent ? (
        <SprintsEmptyState onOpenCircle={onOpenCircle} onFindPeople={onFindPeople} />
      ) : (
        <>
          {needsDecision.length ? (
            <SectionCard icon={<FlashOnRoundedIcon sx={{ fontSize: 20 }} />} title="Needs decision">
              {needsDecision.map((item) => (
                <SprintDecisionCard
                  key={`decision-${item.connectionId}`}
                  item={item}
                  onReview={onReview}
                  onOpenProfile={onOpenProfile}
                />
              ))}
            </SectionCard>
          ) : null}

          {active.length ? (
            <SectionCard icon={<TrackChangesRoundedIcon sx={{ fontSize: 20 }} />} title="Active Accountability">
              {active.map((item) => (
                <ActiveSprintRow
                  key={`active-${item.connectionId}`}
                  item={item}
                  onOpenSprint={onOpenSprint}
                  onMessage={onMessage}
                  onOpenProfile={onOpenProfile}
                />
              ))}
            </SectionCard>
          ) : null}

          {recent.length ? (
            <SectionCard
              icon={<AutorenewRoundedIcon sx={{ fontSize: 20 }} />}
              title="Recent Accountability"
              actionLabel={recent.length > 4 ? (showAllRecent ? "Show less" : "View older") : undefined}
              onActionPress={recent.length > 4 ? () => setShowAllRecent((current) => !current) : undefined}
            >
              {visibleRecent.map((item) => (
                <RecentSprintRow
                  key={`recent-${item.connectionId}`}
                  item={item}
                  onStartAgain={onStartSprint}
                  onReview={onReview}
                  onOpenProfile={onOpenProfile}
                />
              ))}
            </SectionCard>
          ) : null}
        </>
      )}
    </Box>
  );
}
